/**
 * The passers-by who wander the garden: how many, how often, what they look
 * like, and what they say about what they see.
 *
 * Content, not engine. A new project replaces this file and keeps
 * `src/game/visitors.ts` and `src/models/visitors.ts` as they are. Like the
 * rest of `src/content/`, it must not import the game, the app shell, the UI
 * or `three` — `tests/layers.test.ts` fails if it does, so colours are plain
 * hex numbers and everything here is data.
 *
 * ## They are nobody
 *
 * These are anonymous passers-by, not other players and not avatars
 * of published gardens. That is a deliberate design constraint rather than a
 * shortcut: it means the feature works offline, needs no account, and carries
 * no privacy question at all. Do not make a figure here stand for a real
 * person — see `docs/PRIVACY.md` and the world-map rules in AGENTS.md.
 *
 * ## A reaction is never a reward
 *
 * The wording below is the whole payload. A reaction earns no XP, no
 * seedlings, no counter and no badge, and `src/game/visitors.ts` has no way
 * to reach the progression engine. This is what keeps the signal honest: a
 * reaction that paid would be farmed within a day, and a farmed reaction
 * tells the player nothing about their garden. `tests/visitors.test.ts`
 * asserts the module stays unable to pay.
 */

/** Ids are internal only — they are never stored in a save or a share link. */
export type ReactionId =
  | 'delighted'
  | 'pleased'
  | 'unimpressed'
  | 'concerned'
  | 'surprised';

export interface Reaction {
  id: ReactionId;
  /**
   * The face drawn in the bubble. One glyph, rendered to a canvas atlas at
   * build-of-the-scene time — see `src/models/visitors.ts`. Keep it to a
   * character that renders on Android and iOS without a downloaded font.
   */
  glyph: string;
  /** Plain-words meaning. Not shown in the HUD; it is the wording of record. */
  meaning: string;
  /** Bubble tint, hex. Read against both a bright and a dim camera feed. */
  tint: number;
}

/**
 * The reaction set, in the order `game/visitors.ts` documents its priority.
 *
 * Wording is deliberately about the *garden*, never about the player. "Needs
 * water" is an observation; "you neglected this" is a scolding, and a
 * scolding is a punishment for being away — which the growth model exists to
 * avoid (see AGENTS.md, "Growth is a function of the clock").
 */
export const REACTIONS: Record<ReactionId, Reaction> = {
  delighted: {
    id: 'delighted',
    glyph: '\u{1F60D}',
    meaning: 'A full garden, many species, most of it grown.',
    tint: 0xfff0c2,
  },
  pleased: {
    id: 'pleased',
    glyph: '\u{1F642}',
    meaning: 'Coming along nicely.',
    tint: 0xe8f3dc,
  },
  unimpressed: {
    id: 'unimpressed',
    glyph: '\u{1F610}',
    meaning: 'Mostly empty beds.',
    tint: 0xe6e2d8,
  },
  concerned: {
    id: 'concerned',
    glyph: '\u{1F615}',
    meaning: 'Something here has been in the ground a long time untended.',
    tint: 0xf6e0cf,
  },
  surprised: {
    id: 'surprised',
    glyph: '\u{1F632}',
    meaning: 'A rare specimen, or one the weather marked.',
    tint: 0xdcecf6,
  },
};

/** Display order, commonest first. Used only by tests and documentation. */
export const REACTION_ORDER: ReactionId[] = [
  'pleased',
  'delighted',
  'unimpressed',
  'concerned',
  'surprised',
];

/**
 * One kind of passer-by.
 *
 * Every kind is drawn from the same instanced rig, so a kind is a palette and
 * a set of proportions rather than a separate model. Adding one costs no draw
 * calls; it costs `CROWD.maxConcurrent` extra instances at most, which is
 * why the cap below is the number that actually matters.
 */
export interface VisitorKind {
  id: string;
  /** Overall height in metres at the garden's diorama scale. */
  height: number;
  /** Metres per second on the flat. */
  speed: number;
  skin: number;
  /** The upper garment — the largest single area of colour on a figure. */
  garment: number;
  /** Trousers or the lower half of a robe. */
  lower: number;
  /** Headwear; set to the garment colour for a bare head. */
  head: number;
  /**
   * How far the shoulders and hips swing, as a multiple of the base stride.
   * A child bustles; somebody with a camera ambles.
   */
  gait: number;
}

/**
 * Four kinds, sized against the plot rather than against life.
 *
 * The garden is a 0.7–1.6 m diorama, so a life-scale 1.7 m person would stand
 * taller than the whole tier-3 plot is wide and read as a giant wading
 * through a model village. These are sized to the *plants* — about three
 * quarters the height of a mature date palm — which is what makes the plot
 * read as a garden seen from above rather than a table with dolls on it. This
 * is the same call `species.ts` makes about `matureHeight`, for the same
 * reason.
 */
export const VISITOR_KINDS: VisitorKind[] = [
  {
    id: 'walker',
    height: 0.3,
    speed: 0.13,
    skin: 0xb98a63,
    garment: 0xf2efe6,
    lower: 0xe6e0d2,
    head: 0xf7f5ee,
    gait: 1,
  },
  {
    id: 'photographer',
    height: 0.29,
    speed: 0.1,
    skin: 0xa8764f,
    garment: 0x5d7e9c,
    lower: 0x3f4b58,
    head: 0x3f4b58,
    gait: 0.8,
  },
  {
    id: 'parent',
    height: 0.31,
    speed: 0.11,
    skin: 0xc09468,
    garment: 0x6d5d7e,
    lower: 0x50445d,
    head: 0x50445d,
    gait: 0.9,
  },
  {
    id: 'child',
    height: 0.2,
    speed: 0.16,
    skin: 0xcd9f75,
    garment: 0xd8734f,
    lower: 0x9a5138,
    head: 0xd8734f,
    gait: 1.35,
  },
];

export interface CrowdTuning {
  /**
   * Hard cap on figures in the scene at once.
   *
   * The frame rate outranks the feature (AGENTS.md, "The garden's size is a
   * budget"). A full tier-3 plot is already ~405 draw calls with a camera
   * feed underneath, so the crowd is sized to add single figures, not a
   * crowd: the rig is instanced, but the animation cost is per figure and the
   * bubbles are a draw call each.
   */
  maxConcurrent: number;
  /** Seconds between arrivals, before jitter. */
  arrivalEverySeconds: number;
  /** Fraction of that interval the jitter may add or remove, 0–1. */
  arrivalJitter: number;
  /**
   * Nobody arrives at an empty plot. A garden with nothing in it has nothing
   * to react to, and a visitor shrugging at bare soil on the player's first
   * thirty seconds is a bad first impression of a feature meant to be warm.
   */
  minPlantsToVisit: number;
  /** How many plants one visitor stops at before leaving. */
  pausesPerVisit: { min: number; max: number };
  /** Seconds spent facing a plant, bubble showing. */
  pauseSeconds: number;
  /** Seconds the bubble fades in and out over. */
  bubbleFadeSeconds: number;
  /**
   * Metres a visitor keeps clear of a planting slot and of the pavilion ring.
   *
   * Beds sit ~0.47 m apart on every ring (`content/tiers.ts`), so a path
   * threaded between two rings has roughly 0.2 m of room. This is the number
   * that stops a figure standing in a bed.
   */
  clearance: number;
  /** How far beyond the pavilion a visitor appears from and vanishes to. */
  approachMargin: number;
}

export const CROWD: CrowdTuning = {
  maxConcurrent: 3,
  arrivalEverySeconds: 14,
  arrivalJitter: 0.45,
  minPlantsToVisit: 1,
  pausesPerVisit: { min: 1, max: 2 },
  pauseSeconds: 3.2,
  bubbleFadeSeconds: 0.45,
  clearance: 0.085,
  approachMargin: 0.28,
};

/**
 * Where the pavilion ring can actually be walked through, in radians.
 *
 * `models/pavilion.ts` builds three wall arcs with gaps between them, and
 * furnishes two of the gaps — an arcade at ~4.02–4.29 and a dome at ~5.95.
 * These three angles are the clear lines through: the gateway opening itself,
 * then the open part of each remaining gap. They belong here rather than in
 * the engine because the architecture is a client's, so if the arcs or the
 * furniture in that file move, move these with them — a visitor otherwise
 * walks through a wall, which is the one thing about them that would read as
 * broken rather than as decoration.
 */
export const GATE_ANGLES: readonly number[] = [2.33, 4.42, 6.35];
